// ── Settings → Nutrition: Macro & Calorie Adherence Tolerance ────────
// Coach picks how close to target counts as "hit" — drives both the
// check-in adherence streak (server) and the home-tab kcal+macro
// color thresholds (mobile). Two modes: percentage (one band scales
// with each target) or absolute (per-macro kcal / gram amounts).
// Stored on coaches.settings.nutritionTolerance.
//
// Moved out of app.js on 10/20 (standing rule 3: extract from app.js what you touch, the move and the change in
// separate commits). app.js calls initNutritionToleranceCard once in bindEvents and keeps the returned repaint on
// window._renderNutTol, so the coach-profile load can paint the saved value.
//
// ⚠️ THREE THINGS THIS CARD GOT WRONG UNTIL 10/20 (item 5, the client Home Food ring reads this setting):
//  - The mode buttons never turned Save on (only the number boxes were watched), and the kcal / g boxes already
//    showed 100 / 10 / 10 / 10 with nothing saved, so a coach who switched to "Specific kcal / g" to keep exactly
//    those numbers could never save them. The buttons now write hidden inputs that Save watches.
//  - Nothing said the value was not saved. With nothing saved the card says so, and Save stays on so the shown
//    values can be saved as they are.
//  - The optional protein upper limit ("Protein over target: Always counts / Limit it to my tolerance", default
//    off, Clayton 10/17) is saved inside nutritionTolerance as proteinUpperLimit.

export const NUT_TOL_DEFAULTS = {
  mode: 'percent',
  percent: 10,
  absolute: { calories: 100, protein_g: 10, carbs_g: 10, fat_g: 10 },
  proteinUpperLimit: false,
};

export function normalizeNutTol(raw) {
  const r = raw || {};
  const mode = r.mode === 'absolute' ? 'absolute' : 'percent';
  const percent = Number.isFinite(r.percent) && r.percent > 0 ? r.percent : NUT_TOL_DEFAULTS.percent;
  const a = r.absolute || {};
  const absolute = {
    calories:  Number.isFinite(a.calories)  && a.calories  >= 0 ? a.calories  : NUT_TOL_DEFAULTS.absolute.calories,
    protein_g: Number.isFinite(a.protein_g) && a.protein_g >= 0 ? a.protein_g : NUT_TOL_DEFAULTS.absolute.protein_g,
    carbs_g:   Number.isFinite(a.carbs_g)   && a.carbs_g   >= 0 ? a.carbs_g   : NUT_TOL_DEFAULTS.absolute.carbs_g,
    fat_g:     Number.isFinite(a.fat_g)     && a.fat_g     >= 0 ? a.fat_g     : NUT_TOL_DEFAULTS.absolute.fat_g,
  };
  return { mode, percent, absolute, proteinUpperLimit: r.proteinUpperLimit === true };
}

/**
 * Wire the card and paint it once. Returns the repaint, for after the coach row loads.
 * @param {{ getSettings: () => any, getSupabase: () => any, showToast: (msg: string, kind?: string) => void }} deps
 * @returns {() => void}
 */
export function initNutritionToleranceCard({ getSettings, getSupabase, showToast }) {
  /** Point a hidden input at a value and let Save's change tracking hear it. */
  const setHidden = (id, value) => {
    const el = /** @type {HTMLInputElement|null} */ (document.getElementById(id));
    if (!el || el.value === value) return;
    el.value = value;
    el.dispatchEvent(new Event('change'));
  };
  const paintProtein = (on) => {
    document.querySelectorAll('#nut-tol-protein-control [data-nut-tol-protein]').forEach(b => {
      b.classList.toggle('active', b.getAttribute('data-nut-tol-protein') === (on ? 'on' : 'off'));
    });
  };

  function renderNutTol() {
    const saved = getSettings()?.nutritionTolerance;
    const tol = normalizeNutTol(saved);
    const unsaved = !saved || typeof saved !== 'object';
    const note = document.getElementById('nut-tol-unsaved');
    if (note) note.hidden = !unsaved;
    const saveBtn = /** @type {any} */ (document.getElementById('btn-save-nutrition-tolerance'));
    if (saveBtn) {
      saveBtn.dataset.alwaysSave = unsaved ? '1' : '';
      if (unsaved) saveBtn.disabled = false;
    }
    // Mode toggle visual state
    document.querySelectorAll('#nut-tol-mode-control [data-nut-tol-mode]').forEach(btn => {
      btn.classList.toggle('active', btn.getAttribute('data-nut-tol-mode') === tol.mode);
    });
    setHidden('nut-tol-mode-value', tol.mode);
    paintProtein(tol.proteinUpperLimit);
    setHidden('nut-tol-protein-limit', tol.proteinUpperLimit ? 'on' : 'off');
    // Block visibility
    const pBlock = document.getElementById('nut-tol-percent-block');
    const aBlock = document.getElementById('nut-tol-absolute-block');
    if (pBlock) pBlock.style.display = tol.mode === 'percent' ? '' : 'none';
    if (aBlock) aBlock.style.display = tol.mode === 'absolute' ? '' : 'none';
    // Fill inputs
    const inp = (id) => /** @type {HTMLInputElement|null} */ (document.getElementById(id));
    if (inp('nut-tol-percent'))      inp('nut-tol-percent').value      = String(tol.percent);
    if (inp('nut-tol-abs-calories')) inp('nut-tol-abs-calories').value = String(tol.absolute.calories);
    if (inp('nut-tol-abs-protein'))  inp('nut-tol-abs-protein').value  = String(tol.absolute.protein_g);
    if (inp('nut-tol-abs-carbs'))    inp('nut-tol-abs-carbs').value    = String(tol.absolute.carbs_g);
    if (inp('nut-tol-abs-fat'))      inp('nut-tol-abs-fat').value      = String(tol.absolute.fat_g);
  }

  // Mode toggle: clicking a button swaps which block is visible. State
  // persists only when the user hits Save.
  document.querySelectorAll('#nut-tol-mode-control [data-nut-tol-mode]').forEach(btn => {
    btn.addEventListener('click', () => {
      const next = btn.getAttribute('data-nut-tol-mode');
      document.querySelectorAll('#nut-tol-mode-control [data-nut-tol-mode]').forEach(b => {
        b.classList.toggle('active', b === btn);
      });
      const pBlock = document.getElementById('nut-tol-percent-block');
      const aBlock = document.getElementById('nut-tol-absolute-block');
      if (pBlock) pBlock.style.display = next === 'percent' ? '' : 'none';
      if (aBlock) aBlock.style.display = next === 'absolute' ? '' : 'none';
      setHidden('nut-tol-mode-value', next || 'percent');
    });
  });
  document.querySelectorAll('#nut-tol-protein-control [data-nut-tol-protein]').forEach(btn => {
    btn.addEventListener('click', () => {
      const on = btn.getAttribute('data-nut-tol-protein') === 'on';
      paintProtein(on);
      setHidden('nut-tol-protein-limit', on ? 'on' : 'off');
    });
  });

  renderNutTol();

  document.getElementById('btn-save-nutrition-tolerance')?.addEventListener('click', async () => {
    const btn = /** @type {any} */ (document.getElementById('btn-save-nutrition-tolerance'));
    const activeMode = document.querySelector('#nut-tol-mode-control [data-nut-tol-mode].active')?.getAttribute('data-nut-tol-mode') || 'percent';
    const num = (id, fallback) => {
      const v = parseFloat(/** @type {HTMLInputElement|null} */ (document.getElementById(id))?.value ?? '');
      return Number.isFinite(v) && v > 0 ? v : fallback;
    };
    const payload = {
      mode: activeMode,
      percent: num('nut-tol-percent', NUT_TOL_DEFAULTS.percent),
      absolute: {
        calories:  num('nut-tol-abs-calories', NUT_TOL_DEFAULTS.absolute.calories),
        protein_g: num('nut-tol-abs-protein',  NUT_TOL_DEFAULTS.absolute.protein_g),
        carbs_g:   num('nut-tol-abs-carbs',    NUT_TOL_DEFAULTS.absolute.carbs_g),
        fat_g:     num('nut-tol-abs-fat',      NUT_TOL_DEFAULTS.absolute.fat_g),
      },
      proteinUpperLimit: /** @type {HTMLInputElement|null} */ (document.getElementById('nut-tol-protein-limit'))?.value === 'on',
    };

    btn.disabled = true;
    btn.textContent = 'Saving…';
    try {
      const { data: { session: s } } = await getSupabase().auth.getSession();
      if (!s) throw new Error('Not authenticated');
      const res = await fetch('/.netlify/functions/save-coach-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${s.access_token}` },
        body: JSON.stringify({ settings: { nutritionTolerance: payload } }),
      });
      if (!res.ok) throw new Error('Save failed');
      getSettings().nutritionTolerance = payload;
      showToast('Nutrition tolerance saved.', 'success');
      const note = document.getElementById('nut-tol-unsaved');
      if (note) note.hidden = true;
      btn.dataset.alwaysSave = '';
      btn._dirtyReset?.();
    } catch (err) {
      showToast('Failed to save: ' + /** @type {any} */ (err).message, 'error');
      btn.disabled = false;
    }
    btn.textContent = 'Save';
  });

  return renderNutTol;
}
